// Tweestapsverificatie (TOTP) voor owners, admins en platformbeheer (stap G).
//
// Supabase levert de factor; wij bepalen wanneer hij nodig is. Volgorde uit
// het implementatieplan: eerst afdwingen in de app, pas daarna in de
// database-policies, als alle beheerders daadwerkelijk een tweede factor
// hebben. Andersom sluit je jezelf buiten.
//
//   - heeft een beheerder een factor, dan moet hij bij het inloggen de code
//     invoeren voordat de app laadt (niveau aal2)
//   - heeft hij er nog geen, dan laadt de app met een melding en kan hij hem
//     instellen onder Gebruikersbeheer
//
// De magic link is geen tweede factor: e-mailtoegang is precies wat een
// aanvaller al heeft.
import { t } from '../lib/i18n.js';
import { esc } from '../lib/format.js';
import { toast } from '../ui/toast.js';

export const BEHEERROLLEN = ['owner', 'admin'];

// Geeft 'ok' (door), 'code' (code nodig, scherm getoond) of 'geen' (geen factor).
export async function poort(sb, onKlaar) {
  const aal = await sb.auth.mfa?.getAuthenticatorAssuranceLevel?.();
  if (!aal || aal.error) return 'ok';            // MFA niet beschikbaar: niet blokkeren
  const { currentLevel, nextLevel } = aal.data || {};
  if (currentLevel === 'aal2') return 'ok';
  if (nextLevel !== 'aal2') return 'geen';
  toonCodeScherm(sb, onKlaar);
  return 'code';
}

async function eersteFactor(sb) {
  const { data, error } = await sb.auth.mfa.listFactors();
  if (error) throw error;
  return (data?.totp || []).find(f => f.status === 'verified') || null;
}

function toonCodeScherm(sb, onKlaar) {
  for (const id of ['authScreen', 'deniedScreen', 'inviteScreen']) document.getElementById(id)?.classList.remove('active');
  let el = document.getElementById('tweestapScherm');
  if (!el) {
    el = document.createElement('div');
    el.id = 'tweestapScherm';
    el.className = 'denied-screen active';
    document.body.appendChild(el);
  }
  el.classList.add('active');
  document.body.classList.remove('laden');
  el.innerHTML = `<div class="auth-card" style="max-width:380px">
      <h2>${esc(t('tweestap.titel'))}</h2>
      <p style="font-size:13px;color:var(--text-2);margin-bottom:14px">${esc(t('tweestap.uitleg'))}</p>
      <input class="form-input" id="tweestapCode" inputmode="numeric" autocomplete="one-time-code" maxlength="6"
             placeholder="123456" style="text-align:center;font-size:20px;letter-spacing:6px;font-family:var(--mono)">
      <div id="tweestapFout"></div>
      <button class="btn btn-primary" id="tweestapKnop" style="width:100%;justify-content:center;margin-top:12px"><i class="ti ti-shield-check"></i> ${esc(t('tweestap.controleer'))}</button>
      <button class="btn btn-ghost btn-sm" id="tweestapUit" style="margin-top:10px"><i class="ti ti-logout"></i> ${esc(t('auth.uitloggen'))}</button>
    </div>`;
  const veld = el.querySelector('#tweestapCode');
  const controleer = async () => {
    try {
      const factor = await eersteFactor(sb);
      const { error } = await sb.auth.mfa.challengeAndVerify({ factorId: factor.id, code: veld.value.trim() });
      if (error) throw error;
      el.classList.remove('active');
      await onKlaar();
    } catch (e) {
      el.querySelector('#tweestapFout').innerHTML =
        `<div class="banner banner-red" style="margin-top:12px"><i class="ti ti-alert-circle"></i><span>${esc(t('tweestap.fout'))}</span></div>`;
      veld.select();
    }
  };
  el.querySelector('#tweestapKnop').addEventListener('click', controleer);
  veld.addEventListener('keydown', e => { if (e.key === 'Enter') controleer(); });
  el.querySelector('#tweestapUit').addEventListener('click', async () => { await sb.auth.signOut(); location.reload(); });
  setTimeout(() => veld.focus(), 30);
}

// ── Kaart op de pagina Gebruikersbeheer ────────────────────────────────────
export async function renderKaart(sb, orgId) {
  const el = document.getElementById('tweestapKaart');
  if (!el) return;
  let factor = null;
  try { factor = await eersteFactor(sb); } catch { el.innerHTML = ''; return; }
  const { data: zonder } = await sb.rpc('beheerders_zonder_tweestap', { p_org: orgId });
  const lijst = (zonder || []).map(b => `${esc(b.email)} (${esc(b.role)})`).join(', ');
  el.innerHTML = `
    ${factor
      ? `<div class="banner banner-green"><i class="ti ti-shield-check"></i><span>${esc(t('tweestap.aan'))}</span></div>`
      : `<div class="banner banner-amber"><i class="ti ti-shield-exclamation"></i><span>${esc(t('tweestap.uit'))}</span></div>
         <button class="btn btn-primary btn-sm" data-tweestap="instellen"><i class="ti ti-qrcode"></i> ${esc(t('tweestap.instellen'))}</button>`}
    ${lijst ? `<p style="font-size:12px;color:var(--text-3);margin-top:10px">${esc(t('tweestap.zonder'))} ${lijst}</p>` : ''}
    <div id="tweestapInstellen"></div>`;
  el.querySelector('[data-tweestap="instellen"]')?.addEventListener('click', () => instellen(sb, orgId));
}

async function instellen(sb, orgId) {
  const doel = document.getElementById('tweestapInstellen');
  // Een eerder, niet afgemaakte poging staat anders in de weg.
  const { data: bestaand } = await sb.auth.mfa.listFactors();
  for (const f of (bestaand?.all || []).filter(f => f.status !== 'verified')) await sb.auth.mfa.unenroll({ factorId: f.id });
  const { data, error } = await sb.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Tornema' });
  if (error) { toast(t('alg.fout', { melding: error.message }), 'error'); return; }
  doel.innerHTML = `
    <div style="display:flex;gap:16px;align-items:flex-start;flex-wrap:wrap;margin-top:12px">
      <img src="${data.totp.qr_code}" alt="QR" style="width:160px;height:160px;border:1px solid var(--border);border-radius:var(--radius)">
      <div style="flex:1;min-width:200px">
        <p style="font-size:13px;margin-bottom:6px">${esc(t('tweestap.scan'))}</p>
        <p style="font-size:11px;color:var(--text-3);margin-bottom:10px">${esc(t('tweestap.handmatig'))} <code>${esc(data.totp.secret)}</code></p>
        <input class="form-input" id="tweestapNieuweCode" inputmode="numeric" maxlength="6" placeholder="123456" style="max-width:160px;font-family:var(--mono)">
        <button class="btn btn-primary btn-sm" id="tweestapBevestig" style="margin-left:6px"><i class="ti ti-check"></i> ${esc(t('tweestap.bevestig'))}</button>
      </div>
    </div>`;
  doel.querySelector('#tweestapBevestig').addEventListener('click', async () => {
    const { error: fout } = await sb.auth.mfa.challengeAndVerify({ factorId: data.id, code: doel.querySelector('#tweestapNieuweCode').value.trim() });
    if (fout) { toast(t('tweestap.fout'), 'error'); return; }
    toast(t('tweestap.ingesteld'), 'success');
    await renderKaart(sb, orgId);
  });
}
